/** @format */

import React, { useMemo, useState } from "react";
import { Box } from "./MuiCompat";

// Hubs
import Face4OutlinedIcon from "@mui/icons-material/Face4Outlined";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import Diversity3OutlinedIcon from "@mui/icons-material/Diversity3Outlined";

// 3-way shared
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import BedtimeOutlinedIcon from "@mui/icons-material/BedtimeOutlined";
import EmojiEmotionsOutlinedIcon from "@mui/icons-material/EmojiEmotionsOutlined";
import WatchOutlinedIcon from "@mui/icons-material/WatchOutlined";

// Appearance + Productivity
import LocalCafeOutlinedIcon from "@mui/icons-material/LocalCafeOutlined";
import DirectionsRunOutlinedIcon from "@mui/icons-material/DirectionsRunOutlined";

// Appearance + Relationships
import SpaOutlinedIcon from "@mui/icons-material/SpaOutlined";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import SelfImprovementOutlinedIcon from "@mui/icons-material/SelfImprovementOutlined";

// Productivity + Relationships
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import HearingOutlinedIcon from "@mui/icons-material/HearingOutlined";

// Appearance only
import FaceRetouchingNaturalOutlinedIcon from "@mui/icons-material/FaceRetouchingNaturalOutlined";
import CheckroomOutlinedIcon from "@mui/icons-material/CheckroomOutlined";
import ContentCutOutlinedIcon from "@mui/icons-material/ContentCutOutlined";
import LocalDrinkOutlinedIcon from "@mui/icons-material/LocalDrinkOutlined";
import AccessibilityNewOutlinedIcon from "@mui/icons-material/AccessibilityNewOutlined";

// Productivity only
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import CenterFocusStrongOutlinedIcon from "@mui/icons-material/CenterFocusStrongOutlined";
import DesktopMacOutlinedIcon from "@mui/icons-material/DesktopMacOutlined";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";

// Relationships only
import FamilyRestroomOutlinedIcon from "@mui/icons-material/FamilyRestroomOutlined";
import HandshakeOutlinedIcon from "@mui/icons-material/HandshakeOutlined";
import FavoriteBorderOutlinedIcon from "@mui/icons-material/FavoriteBorderOutlined";

const fontInter = `'Inter', ui-sans-serif, system-ui, -apple-system, sans-serif`;
const fontMono = `'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace`;

type IconCmp = React.ComponentType<{
  sx?: object;
  fontSize?: "inherit" | "small" | "medium" | "large";
}>;

interface NetNode {
  id: string;
  label: string;
  type: "hub" | "dep";
  x: number;
  y: number;
  Icon: IconCmp;
}

const nodes: NetNode[] = [
  // ===== Hubs (triangle) =====
  // Productivity (top-left) and Relationships (top-right) sit on the same
  // axis — the canonical "work / life balance" axis. Appearance lives at
  // the bottom-center as the third corner.
  {
    id: "productivity",
    label: "Productivity",
    type: "hub",
    x: 22,
    y: 32,
    Icon: BoltOutlinedIcon,
  },
  {
    id: "relationships",
    label: "Relationships",
    type: "hub",
    x: 78,
    y: 32,
    Icon: Diversity3OutlinedIcon,
  },
  {
    id: "appearance",
    label: "Appearance",
    type: "hub",
    x: 50,
    y: 82,
    Icon: Face4OutlinedIcon,
  },

  // ===== 3-way shared (central spine) =====
  {
    id: "calendar",
    label: "calendar",
    type: "dep",
    x: 50,
    y: 6,
    Icon: CalendarMonthOutlinedIcon,
  },
  {
    id: "sleep",
    label: "sleep",
    type: "dep",
    x: 50,
    y: 26,
    Icon: BedtimeOutlinedIcon,
  },
  {
    id: "mood",
    label: "mood",
    type: "dep",
    x: 50,
    y: 46,
    Icon: EmojiEmotionsOutlinedIcon,
  },
  {
    id: "wearable",
    label: "wearable",
    type: "dep",
    x: 50,
    y: 62,
    Icon: WatchOutlinedIcon,
  },

  // ===== Productivity + Relationships (top axis, between top hubs) =====
  {
    id: "meetings",
    label: "meetings",
    type: "dep",
    x: 32,
    y: 14,
    Icon: GroupsOutlinedIcon,
  },
  {
    id: "communication",
    label: "communication",
    type: "dep",
    x: 44,
    y: 18,
    Icon: ForumOutlinedIcon,
  },
  {
    id: "collaboration",
    label: "collaboration",
    type: "dep",
    x: 56,
    y: 18,
    Icon: LinkOutlinedIcon,
  },
  {
    id: "listening",
    label: "listening",
    type: "dep",
    x: 68,
    y: 14,
    Icon: HearingOutlinedIcon,
  },

  // ===== Appearance + Productivity (left-down axis, between P and A) =====
  {
    id: "caffeine",
    label: "caffeine",
    type: "dep",
    x: 28,
    y: 50,
    Icon: LocalCafeOutlinedIcon,
  },
  {
    id: "exercise",
    label: "exercise",
    type: "dep",
    x: 38,
    y: 68,
    Icon: DirectionsRunOutlinedIcon,
  },

  // ===== Appearance + Relationships (right-down axis, between R and A) =====
  {
    id: "selfcare",
    label: "self-care",
    type: "dep",
    x: 74,
    y: 48,
    Icon: SpaOutlinedIcon,
  },
  {
    id: "confidence",
    label: "confidence",
    type: "dep",
    x: 64,
    y: 62,
    Icon: AutoAwesomeOutlinedIcon,
  },
  {
    id: "presence",
    label: "presence",
    type: "dep",
    x: 60,
    y: 76,
    Icon: SelfImprovementOutlinedIcon,
  },

  // ===== Productivity only (left perimeter, near Productivity hub) =====
  {
    id: "deadlines",
    label: "deadlines",
    type: "dep",
    x: 5,
    y: 18,
    Icon: TimerOutlinedIcon,
  },
  {
    id: "focus",
    label: "focus",
    type: "dep",
    x: 4,
    y: 36,
    Icon: CenterFocusStrongOutlinedIcon,
  },
  {
    id: "workspace",
    label: "workspace",
    type: "dep",
    x: 5,
    y: 52,
    Icon: DesktopMacOutlinedIcon,
  },
  {
    id: "tools",
    label: "tools",
    type: "dep",
    x: 10,
    y: 68,
    Icon: BuildOutlinedIcon,
  },

  // ===== Relationships only (right perimeter, near Relationships hub) =====
  {
    id: "family",
    label: "family",
    type: "dep",
    x: 95,
    y: 18,
    Icon: FamilyRestroomOutlinedIcon,
  },
  {
    id: "friends",
    label: "friends",
    type: "dep",
    x: 96,
    y: 36,
    Icon: HandshakeOutlinedIcon,
  },
  {
    id: "partner",
    label: "partner",
    type: "dep",
    x: 96,
    y: 52,
    Icon: FavoriteBorderOutlinedIcon,
  },

  // ===== Appearance only (bottom perimeter, near Appearance hub) =====
  {
    id: "skincare",
    label: "skincare",
    type: "dep",
    x: 22,
    y: 90,
    Icon: FaceRetouchingNaturalOutlinedIcon,
  },
  {
    id: "grooming",
    label: "grooming",
    type: "dep",
    x: 35,
    y: 96,
    Icon: ContentCutOutlinedIcon,
  },
  {
    id: "wardrobe",
    label: "wardrobe",
    type: "dep",
    x: 65,
    y: 96,
    Icon: CheckroomOutlinedIcon,
  },
  {
    id: "hydration",
    label: "hydration",
    type: "dep",
    x: 78,
    y: 90,
    Icon: LocalDrinkOutlinedIcon,
  },
  {
    id: "posture",
    label: "posture",
    type: "dep",
    x: 50,
    y: 96,
    Icon: AccessibilityNewOutlinedIcon,
  },
];

const edges: Array<[string, string]> = [
  // 3-way — every hub touches calendar / sleep / mood / wearable.
  ["appearance", "calendar"],
  ["productivity", "calendar"],
  ["relationships", "calendar"],
  ["appearance", "sleep"],
  ["productivity", "sleep"],
  ["relationships", "sleep"],
  ["appearance", "mood"],
  ["productivity", "mood"],
  ["relationships", "mood"],
  ["appearance", "wearable"],
  ["productivity", "wearable"],
  ["relationships", "wearable"],

  // Appearance + Productivity
  ["appearance", "caffeine"],
  ["productivity", "caffeine"],
  ["appearance", "exercise"],
  ["productivity", "exercise"],

  // Appearance + Relationships
  ["appearance", "selfcare"],
  ["relationships", "selfcare"],
  ["appearance", "confidence"],
  ["relationships", "confidence"],
  ["appearance", "presence"],
  ["relationships", "presence"],

  // Productivity + Relationships
  ["productivity", "meetings"],
  ["relationships", "meetings"],
  ["productivity", "communication"],
  ["relationships", "communication"],
  ["productivity", "collaboration"],
  ["relationships", "collaboration"],
  ["productivity", "listening"],
  ["relationships", "listening"],

  // Appearance only
  ["appearance", "skincare"],
  ["appearance", "grooming"],
  ["appearance", "wardrobe"],
  ["appearance", "hydration"],
  ["appearance", "posture"],

  // Productivity only
  ["productivity", "deadlines"],
  ["productivity", "focus"],
  ["productivity", "workspace"],
  ["productivity", "tools"],

  // Relationships only
  ["relationships", "family"],
  ["relationships", "friends"],
  ["relationships", "partner"],
];

const Problem: React.FC = () => {
  const [hovered, setHovered] = useState<string | null>(null);

  const adjacency = useMemo(() => {
    const map = new Map<string, Set<string>>();
    nodes.forEach((n) => map.set(n.id, new Set()));
    edges.forEach(([a, b]) => {
      map.get(a)!.add(b);
      map.get(b)!.add(a);
    });
    return map;
  }, []);

  const nodeById = useMemo(() => {
    const map = new Map<string, NetNode>();
    nodes.forEach((n) => map.set(n.id, n));
    return map;
  }, []);

  const isHighlighted = (id: string) => {
    if (!hovered) return null;
    if (hovered === id) return "primary";
    if (adjacency.get(hovered)?.has(id)) return "secondary";
    return "dim";
  };

  const isEdgeHighlighted = (a: string, b: string) => {
    if (!hovered) return null;
    if (hovered === a || hovered === b) return "active";
    return "dim";
  };

  // Precompute the SVG path string for every edge once. Without this, every
  // hover state change recomputes sqrt + path strings.
  const edgePaths = useMemo(() => {
    return edges.map(([a, b]) => {
      const A = nodeById.get(a)!;
      const B = nodeById.get(b)!;
      const mx = (A.x + B.x) / 2;
      const my = (A.y + B.y) / 2;
      const dx = B.x - A.x;
      const dy = B.y - A.y;
      const len = Math.sqrt(dx * dx + dy * dy);
      const nx = (-dy / len) * 4;
      const ny = (dx / len) * 4;
      return `M${A.x},${A.y} Q${mx + nx},${my + ny} ${B.x},${B.y}`;
    });
  }, [nodeById]);

  // Each hub radiates a soft glow down its outgoing edges.
  const hubColors: Record<string, string> = {
    appearance: "86, 194, 255", // sky-signal cyan
    productivity: "169, 119, 255", // violet-leaning
    relationships: "120, 220, 200", // mint-leaning cyan
  };

  return (
    <Box
      component="section"
      sx={{
        position: "relative",
        py: { xs: 12, md: 18 },
        overflow: "hidden",
      }}
    >
      {/* Per-section violet atmosphere — sits behind content, ahead of the
          global page background. Gives the section its own light source. */}
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          left: "50%",
          top: "55%",
          transform: "translate(-50%, -50%)",
          width: { xs: "140vw", md: "1400px" },
          height: { xs: "90vh", md: "900px" },
          maxWidth: "1600px",
          pointerEvents: "none",
          zIndex: 0,
          background:
            "radial-gradient(60% 55% at 50% 50%, rgba(82, 48, 145, 0.45) 0%, rgba(38, 22, 78, 0.18) 45%, rgba(4, 5, 6, 0) 75%)",
        }}
      />
      {/* A thinner cyan halo behind the diagram center — picks up the hero's
          sky-signal palette for continuity. */}
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          left: "50%",
          top: "62%",
          transform: "translate(-50%, -50%)",
          width: { xs: "100vw", md: "900px" },
          height: { xs: "60vh", md: "560px" },
          pointerEvents: "none",
          zIndex: 0,
          background:
            "radial-gradient(50% 50% at 50% 50%, rgba(86, 194, 255, 0.16) 0%, rgba(4, 5, 6, 0) 70%)",
        }}
      />

      <Box
        sx={{
          position: "relative",
          zIndex: 2,
          maxWidth: "var(--page-max-width)",
          mx: "auto",
          px: { xs: 3, sm: 5, md: 8 },
        }}
      >
        <Box
          component="h2"
          sx={{
            m: 0,
            mx: "auto",
            maxWidth: "880px",
            textAlign: "center",
            fontFamily: fontInter,
            fontWeight: 600,
            fontSize: "clamp(2rem, 4.5vw, 3.5rem)",
            lineHeight: "var(--leading-heading)",
            letterSpacing: "var(--tracking-heading-lg)",
            color: "var(--color-snow)",
          }}
        >
          Life is complex.
        </Box>
        <Box
          sx={{
            mt: 2,
            mx: "auto",
            maxWidth: "640px",
            textAlign: "center",
            fontFamily: fontInter,
            fontSize: "var(--text-subheading)",
            lineHeight: "var(--leading-subheading)",
            letterSpacing: "var(--tracking-subheading)",
            color: "var(--color-slate-200)",
          }}
        >
          Your goals, constraints, and decisions are all connected. But you have
          been forced to manage them separately through trial and error.
        </Box>

        <Box
          sx={{
            mt: { xs: 7, md: 10 },
            position: "relative",
            width: "100%",
            aspectRatio: { xs: "1 / 1.05", md: "16 / 9" },
            maxHeight: { md: 640 },
            // Soft vignette so peripheral nodes fade into the void rather
            // than terminating against a hard rectangle.
            maskImage:
              "radial-gradient(ellipse 78% 82% at 50% 50%, #000 58%, transparent 100%)",
            WebkitMaskImage:
              "radial-gradient(ellipse 78% 82% at 50% 50%, #000 58%, transparent 100%)",
            "@keyframes problemEdgeFlow": {
              "0%": { strokeDashoffset: 0 },
              "100%": { strokeDashoffset: -32 },
            },
          }}
        >
          <Box
            component="svg"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            sx={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              overflow: "visible",
              pointerEvents: "none",
            }}
          >
            <defs>
              {nodes
                .filter((n) => n.type === "hub")
                .map((h) => (
                  <radialGradient
                    key={h.id}
                    id={`problemHubGlow-${h.id}`}
                    cx={h.x}
                    cy={h.y}
                    r={95}
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop
                      offset="0%"
                      stopColor={`rgba(${hubColors[h.id]}, 1)`}
                    />
                    <stop
                      offset="50%"
                      stopColor={`rgba(${hubColors[h.id]}, 0.65)`}
                    />
                    <stop
                      offset="100%"
                      stopColor={`rgba(${hubColors[h.id]}, 0.32)`}
                    />
                  </radialGradient>
                ))}
            </defs>

            {edges.map(([a, b], i) => {
              const state = isEdgeHighlighted(a, b);
              const A = nodeById.get(a)!;
              const hubId = A.type === "hub" ? a : b;
              const opacity =
                state === "active" ? 1 : state === "dim" ? 0.08 : 0.9;
              const isActive = state === "active";
              return (
                <path
                  key={i}
                  d={edgePaths[i]}
                  fill="none"
                  stroke={`url(#problemHubGlow-${hubId})`}
                  strokeWidth={isActive ? 0.6 : 0.36}
                  strokeOpacity={opacity}
                  strokeLinecap="round"
                  vectorEffect="non-scaling-stroke"
                  style={{
                    transition:
                      "stroke-opacity 280ms ease, stroke-width 280ms ease",
                    strokeDasharray: isActive ? "1 2" : "0",
                    animation: isActive
                      ? "problemEdgeFlow 1.4s linear infinite"
                      : "none",
                  }}
                />
              );
            })}
          </Box>

          {nodes.map((n) => {
            const state = isHighlighted(n.id);
            const isHub = n.type === "hub";
            const Icon = n.Icon;

            // Depth-of-field: peripheral nodes are slightly out of focus and
            // dim, except when the user is engaging the graph (hover state).
            const depBaseOpacity = 0.62;
            const depBaseBlur = "blur(0.45px)";
            const labelOpacity = isHub
              ? state === "dim"
                ? 0.32
                : 1
              : state === "primary"
                ? 1
                : state === "secondary"
                  ? 1
                  : state === "dim"
                    ? 0.14
                    : depBaseOpacity;
            const nodeBlur = isHub
              ? "none"
              : state === "secondary" || state === "primary"
                ? "none"
                : depBaseBlur;
            const labelScale = state === "primary" ? 1.05 : 1;

            const hubAccent = isHub ? hubColors[n.id] : null;

            return (
              <Box
                key={n.id}
                onMouseEnter={() => setHovered(n.id)}
                onMouseLeave={() => setHovered(null)}
                sx={{
                  position: "absolute",
                  left: `${n.x}%`,
                  top: `${n.y}%`,
                  transform: `translate(-50%, -50%) scale(${labelScale})`,
                  cursor: "pointer",
                  opacity: labelOpacity,
                  filter: nodeBlur,
                  transition:
                    "opacity 280ms ease, transform 280ms ease, filter 280ms ease",
                  pointerEvents: "auto",
                  zIndex: isHub ? 3 : 2,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                {isHub ? (
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "10px",
                      pl: "12px",
                      pr: "18px",
                      py: "10px",
                      borderRadius: "11px",
                      backgroundColor: "rgba(11, 12, 14, 0.96)",
                      backdropFilter: "blur(8px)",
                      WebkitBackdropFilter: "blur(8px)",
                      fontFamily: fontInter,
                      fontSize: { xs: "15px", md: "18px" },
                      fontWeight: 600,
                      letterSpacing: "-0.005em",
                      color: "var(--color-snow)",
                      whiteSpace: "nowrap",
                      // Raycast keyboard-key shadow stack — outer drop +
                      // inset bevel — gives the chip a physical, pressable
                      // quality so hubs read as anchors, not labels.
                      boxShadow: `
                        rgba(0, 0, 0, 0.45) 0px 1.5px 0.5px 2.5px,
                        rgb(0, 0, 0) 0px 0px 0.5px 1px,
                        rgba(0, 0, 0, 0.3) 0px 2px 1px 1px inset,
                        rgba(255, 255, 255, 0.18) 0px 1px 1px 1px inset,
                        rgba(${hubAccent}, 0.22) 0px 0px 28px 4px
                      `,
                    }}
                  >
                    <Icon
                      sx={{
                        fontSize: 20,
                        color: `rgba(${hubAccent}, 1)`,
                      }}
                    />
                    {n.label}
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: "5px",
                    }}
                  >
                    <Icon
                      sx={{
                        fontSize: 20,
                        color: "rgba(255, 255, 255, 0.7)",
                        filter: "drop-shadow(0 1px 6px rgba(4, 5, 6, 0.85))",
                      }}
                    />
                    <Box
                      sx={{
                        fontFamily: fontMono,
                        fontSize: { xs: "9.5px", md: "10px" },
                        fontWeight: 400,
                        letterSpacing: "0.06em",
                        textTransform: "lowercase",
                        color: "var(--color-slate-200)",
                        whiteSpace: "nowrap",
                        textShadow: "0 1px 8px rgba(4, 5, 6, 0.95)",
                      }}
                    >
                      {n.label}
                    </Box>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export default Problem;
